import { useState } from "react";
import { Mail, MapPin, Phone, Send } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import PublicNavbar from "../components/PublicNavbar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form)
      });

      if (response.ok) {
        setSubmitted(true);
        toast.success("Message sent successfully!");
        setForm({ name: "", email: "", phone: "", subject: "", message: "" });
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to send message");
      }
    } catch (error) {
      toast.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0B] relative overflow-hidden">
      <PublicNavbar />
      
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
        <div className="absolute top-1/3 left-1/3 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#D4AF37]/3 rounded-full blur-3xl"></div>
      </div>

      <main className="relative z-10 pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#FAFAFA] mb-6"
              style={{ fontFamily: "'Manrope', sans-serif" }}
              data-testid="contact-title"
            >
              Get in <span className="text-[#D4AF37]">Touch</span>
            </h1>
            
            <p className="text-lg md:text-xl text-[#A1A1AA] max-w-2xl mx-auto" data-testid="contact-description">
              Have a project in mind? Let&apos;s create something extraordinary together.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="text-center p-6 bg-[#121214] border border-[#27272A] rounded-xl">
              <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mx-auto mb-4">
                <Mail size={24} className="text-[#D4AF37]" />
              </div>
              <h3 className="text-lg font-semibold text-[#FAFAFA] mb-2">Email</h3>
              <p className="text-[#A1A1AA] text-sm">contact@dopeinhd.com</p>
            </div>
            
            <div className="text-center p-6 bg-[#121214] border border-[#27272A] rounded-xl">
              <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mx-auto mb-4">
                <Phone size={24} className="text-[#D4AF37]" />
              </div>
              <h3 className="text-lg font-semibold text-[#FAFAFA] mb-2">Phone</h3>
              <p className="text-[#A1A1AA] text-sm">Available by appointment</p>
            </div>
            
            <div className="text-center p-6 bg-[#121214] border border-[#27272A] rounded-xl">
              <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mx-auto mb-4">
                <MapPin size={24} className="text-[#D4AF37]" />
              </div>
              <h3 className="text-lg font-semibold text-[#FAFAFA] mb-2">Location</h3>
              <p className="text-[#A1A1AA] text-sm">Worldwide</p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="max-w-xl mx-auto p-8 bg-[#121214] border border-[#27272A] rounded-xl">
            {submitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mx-auto mb-4">
                  <Send size={32} className="text-[#D4AF37]" />
                </div>
                <h2 className="text-2xl font-semibold text-[#FAFAFA] mb-2">Message Sent!</h2>
                <p className="text-[#A1A1AA] mb-6">
                  Thank you for reaching out. We&apos;ll get back to you soon.
                </p>
                <Button
                  onClick={() => setSubmitted(false)}
                  variant="outline"
                  className="border-[#27272A] text-[#FAFAFA]"
                >
                  Send Another Message
                </Button>
              </div>
            ) : (
              <>
                <h2 className="text-2xl font-semibold text-[#FAFAFA] mb-6 text-center">Send Us a Message</h2>
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#A1A1AA]">Name *</Label>
                      <Input 
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        required
                        className="mt-1 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA] placeholder-[#71717A]"
                        placeholder="Your name"
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <Label className="text-[#A1A1AA]">Email *</Label>
                      <Input 
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={handleChange}
                        required
                        className="mt-1 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA] placeholder-[#71717A]"
                        placeholder="your@email.com"
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#A1A1AA]">Phone</Label>
                      <Input 
                        name="phone"
                        type="tel"
                        value={form.phone}
                        onChange={handleChange}
                        className="mt-1 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA] placeholder-[#71717A]"
                        placeholder="+1 (555) 000-0000"
                        data-testid="contact-phone-input"
                      />
                    </div>
                    <div>
                      <Label className="text-[#A1A1AA]">Subject</Label>
                      <Input 
                        name="subject"
                        value={form.subject}
                        onChange={handleChange}
                        className="mt-1 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA] placeholder-[#71717A]"
                        placeholder="What's this about?"
                        data-testid="contact-subject-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-[#A1A1AA]">Message *</Label>
                    <Textarea 
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      required
                      className="mt-1 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA] placeholder-[#71717A] min-h-[120px]"
                      placeholder="Tell us about your project..."
                      data-testid="contact-message-input"
                    />
                  </div>

                  <Button 
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 bg-gradient-to-r from-[#D4AF37] to-[#B8952F] text-[#0A0A0B] font-semibold hover:from-[#E5C558] hover:to-[#D4AF37]"
                    data-testid="contact-submit-btn"
                  >
                    {loading ? "Sending..." : "Send Message"}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </main>

      <footer className="relative z-10 text-center py-8 border-t border-[#27272A]">
        <p className="text-[#71717A] text-xs">
          © 2025 DopeInHD Incorporated. All rights reserved.
        </p>
      </footer>
    </div>
  );
};

export default Contact;
